import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { AllRawData } from '../../raw-types.ts';
import type {
  FolkvangrAttackPatternData,
  FolkvangrAttackType,
  FolkvangrAoeZoneParams,
  FolkvangrBarrageParams,
  FolkvangrBossData,
  FolkvangrBossPatternEntry,
  FolkvangrLightningParams,
  FolkvangrSlamParams,
  FolkvangrSummonParams,
  FolkvangrGradeStatData,
  FolkvangrRankType,
  FolkvangrRewardTierData,
  FolkvangrOmenData,
  FolkvangrOmenEffect,
  FolkvangrSeasonData,
} from '../../../master-data/types/folkvangr.types.ts';
import {
  FOLKVANGR_ATTACK_TYPES,
  FOLKVANGR_EFFECT_TYPES,
} from '../../../master-data/types/folkvangr.types.ts';

// 폴크방 마스터데이터 코덱. 필드는 append-only — 신규 필드는 항상 배열 끝에 붙인다.

export const FolkvangrBossSchema = defineSchema({
  name: 'serverFolkvangrBosses',
  fields: [
    ['id', 'number'],
    ['nameKey', 'string'],
    ['resourceId', 'string?'],
    ['bg', 'string?'],
    ['blessedMetaTagId', 'number?'],
    ['blessedDamageMul', 'number'],
    ['blessedHpMul', 'number'],
    ['durationSec', 'number'],
    ['scoreMul', 'number'],
    // ⚠️ tombstone — 페이즈(각성 단계) 모델은 폐지됐지만 **슬롯은 지킨다.**
    // 이 튜플은 위치로만 읽히고 V8 verse 에 이미 이 순서로 시딩돼 있어, 슬롯을
    // 빼면 뒤의 `patterns` 가 이 자리의 값을 읽어 보스가 통째로 오염된다
    // (`FolkvangrSeasonSchema.milestoneTableId` 와 같은 판단). 항상 null 이 실린다.
    ['phases', { kind: 'json' }],
    ['patterns', { kind: 'json' }],
  ],
  lookupKeys: ['id'],
});

export const FolkvangrGradeStatSchema = defineSchema({
  name: 'serverFolkvangrGradeStats',
  fields: [
    ['grade', 'number'],
    ['baseDps', 'number'],
    ['baseHp', 'number'],
    ['levelDpsK', 'number'],
    ['levelHpK', 'number'],
    ['btDpsK', 'number'],
    ['btHpK', 'number'],
  ],
  lookupKeys: ['grade'],
});

export const FolkvangrOmenSchema = defineSchema({
  name: 'serverFolkvangrRunes',
  fields: [
    ['id', 'number'],
    ['omenPoolId', 'number'],
    ['nameKey', 'string'],
    ['descriptionKey', 'string'],
    ['icon', 'string?'],
    ['effects', { kind: 'json' }],
    ['selectionWeight', 'number'],
    ['isActive', 'boolean'],
  ],
  lookupKeys: ['id'],
});

export const FolkvangrSeasonSchema = defineSchema({
  name: 'serverFolkvangrSeasons',
  fields: [
    ['id', 'number'],
    ['name', 'string'],
    ['startTime', 'number'],
    ['endTime', 'number'],
    // tombstone: `folkvangr_schedule` 폐지(2026-09) 이후에도 슬롯은 유지한다 —
    // 아래 `milestoneTableId` 와 같은 판단이며, 이유는 그 주석에 있다. 항상 null.
    ['scheduleId', 'number?'],
    ['omenPoolId', 'number'],
    ['rewardTableId', 'string'],
    // tombstone: 마일스톤 폐지(2026-09) 이후에도 슬롯은 유지한다. V8 verse 에는
    // 이미 이 자리에 `milestoneTableId` 값이 시딩된 16슬롯 튜플이 존재하고,
    // generic-codec 의 decode 는 길이/버전 검사 없는 순수 위치 기반이라 이 슬롯을
    // 지우면 뒤 필드가 전부 한 칸씩 밀려 읽힌다(resetHourUtc 등이 오염됨).
    // 값은 더 이상 읽지 않는다 — 자리만 지킨다. schema-meta.ts 의
    // "append-only positional list, 중간 삭제·재배열 금지" 규칙 참조.
    ['milestoneTableId', 'string?'],
    ['playReward', { kind: 'json' }],
    ['dailyFreeCount', 'number'],
    ['dailyAdCount', 'number'],
    ['dailyTicketMax', 'number'],
    ['probeSampleCount', 'number'],
    ['probeDensity', 'number'],
    ['resetHourUtc', 'number'],
    // tombstone: 접힌 편성 배열이 실리던 자리. 위 `scheduleId` 와 같은 이유로 남긴다.
    ['schedule', { kind: 'json' }],
    // append-only — 아래 3개는 편성표를 대신해 시즌 행이 직접 드는 값이다.
    ['bossId', 'number'],
    ['omenId', 'number?'],
    ['blessedMetaTagId', 'number?'],
  ],
  lookupKeys: ['id'],
});

// ---------------------------------------------------------------- raw row 형상

export interface RawFolkvangrBossRow {
  id: number;
  name_key: string;
  resource_id: string | null;
  bg: string | null;
  blessed_meta_tag_id: number | null;
  blessed_damage_mul: number;
  blessed_hp_mul: number;
  duration_sec: number;
  score_mul: number;
}

export interface RawFolkvangrAttackPatternRow {
  id: number;
  name: string;
  type: string;
  params: unknown;
}

export interface RawFolkvangrBossPatternRow {
  boss_id: number;
  at_sec: number;
  pattern_id: number;
  damage: number;
  repeat_every_sec: number | null;
  repeat_count: number | null;
}

export interface RawFolkvangrGradeStatRow {
  grade: number;
  base_dps: number;
  base_hp: number;
  level_dps_k: number | null;
  level_hp_k: number | null;
  bt_dps_k: number | null;
  bt_hp_k: number | null;
}

export interface RawFolkvangrOmenRow {
  id: number;
  omen_pool_id: number;
  name_key: string;
  description_key: string;
  icon: string | null;
  effects: FolkvangrOmenEffect[] | null;
  selection_weight: number;
  is_active: boolean;
}

export interface RawFolkvangrSeasonRow {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
  omen_pool_id: number;
  reward_table_id: string;
  milestone_table_id: string | null;
  boss_id: number;
  omen_id: number | null;
  blessed_meta_tag_id: number | null;
  play_reward: Record<string, unknown> | null;
  daily_free_count: number;
  daily_ad_count: number;
  daily_ticket_max: number;
  probe_sample_count: number;
  probe_density: number;
  reset_hour_utc: number;
}

export interface RawFolkvangrRewardRow {
  reward_table_id: string;
  tier_order: number;
  rank_type: string;
  rank_from: number;
  rank_to: number;
  rewards: Record<string, unknown> | null;
}

/**
 * 시즌 def. 타입 실체는 `master-data/types/folkvangr.types` 의 `FolkvangrSeasonData` —
 * 오늘의 보스·계시를 고르는 공유 로직(`@rb/shared/folkvangr/schedule`)이 클라·서버
 * 양쪽 발행물을 같은 타입으로 받아야 해서 타입 모듈 쪽에 둔다.
 */
export type FolkvangrSeasonDef = FolkvangrSeasonData;

// ---------------------------------------------------------------- raw → def

function optText(v: unknown): string | undefined {
  const s = String(v ?? '').trim();
  return s ? s : undefined;
}

function parseEpoch(value: string, field: string): number {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) {
    throw new Error(`folkvangr_season.${field}: 시각 파싱 실패 — "${value}"`);
  }
  return ms;
}

/**
 * boss 행 + 자식 phase 행 → FolkvangrBossData.
 *
 * 2번째 인자는 `AllRawData` 전체다 — `stageFromRaw` 와 같은 규약이라
 * build-json 의 `FROM_RAW` 제네릭 디스패치(`(row, raw) => def`)에 그대로 꽂힌다.
 * 자식 행 배열을 직접 받으면 그 디스패치에 못 태우고, 결국 서버 시드가
 * 코덱 튜플이 아닌 plain object 로 나가버린다.
 */
/**
 * `folkvangr_attack_pattern.params`(JSONB) 를 타입별로 검증해 좁힌다.
 *
 * DB 제약은 `jsonb_typeof(params) = 'object'` 뿐이라 오타(`intervalMS`)나 누락이
 * 그대로 통과한다. 통과하면 런타임이 `undefined` 를 그대로 쓰게 되어 "장판이
 * 안 깔린다 / 한 발도 안 나간다" 처럼 **조용히** 망가진다 — `assertRuneEffects` 와
 * 같은 이유로 빌드 시점에 던진다.
 *
 * 필수 필드가 없으면 hard-fail 이고, 선택 필드만 기본값을 채운다.
 */
function assertPatternParams(
  id: number,
  type: FolkvangrAttackType,
  raw: unknown
): FolkvangrAttackPatternData['params'] {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`folkvangr_attack_pattern(id=${id}).params: 객체가 아니다`);
  }
  const obj = raw as Record<string, unknown>;

  /** 필수 수치. 없거나 유한하지 않으면 던진다. */
  const num = (field: string): number => {
    const v = obj[field];
    const n = typeof v === 'string' ? Number(v) : v;
    if (typeof n !== 'number' || !Number.isFinite(n)) {
      throw new Error(
        `folkvangr_attack_pattern(id=${id}, type=${type}).params.${field}: ` +
          `수치가 필요하다 — 받은 값 ${JSON.stringify(v)}`
      );
    }
    return n;
  };
  /** 선택 수치. 비어 있으면 기본값. */
  const optNum = (field: string, fallback: number): number =>
    obj[field] === undefined || obj[field] === null ? fallback : num(field);

  switch (type) {
    case 'AOE_ZONE': {
      const params: FolkvangrAoeZoneParams = {
        count: num('count'),
        radius: num('radius'),
        durationMs: num('durationMs'),
        tickIntervalMs: num('tickIntervalMs'),
        windupMs: optNum('windupMs', 0),
        spawnWindowMs: optNum('spawnWindowMs', 0),
      };
      const resourceKey = optText(obj.resourceKey);
      if (resourceKey) params.resourceKey = resourceKey;
      return params;
    }
    case 'BARRAGE': {
      const params: FolkvangrBarrageParams = {
        count: num('count'),
        intervalMs: num('intervalMs'),
        speed: num('speed'),
        pierce: optNum('pierce', 1),
        radius: optNum('radius', 8),
        windupMs: optNum('windupMs', 0),
      };
      const resourceKey = optText(obj.resourceKey);
      if (resourceKey) params.resourceKey = resourceKey;
      return params;
    }
    case 'SUMMON': {
      const params: FolkvangrSummonParams = {
        enemyId: num('enemyId'),
        count: num('count'),
        intervalMs: num('intervalMs'),
        speed: num('speed'),
        health: num('health'),
        contactIntervalMs: optNum('contactIntervalMs', 1000),
      };
      return params;
    }
    case 'LIGHTNING': {
      const params: FolkvangrLightningParams = {
        targets: num('targets'),
        strikes: num('strikes'),
        intervalMs: num('intervalMs'),
        radius: optNum('radius', 0),
      };
      return params;
    }
    case 'SLAM': {
      // 필수는 "보스가 어떻게 움직이는가" 셋뿐이다. 나머지는 기본값이 있어야
      // 최소 편성("솟았다 찍는다")이 한 줄로 적힌다 — 넉백 0·반경 0(전체)·1회.
      const params: FolkvangrSlamParams = {
        riseHeight: num('riseHeight'),
        riseMs: num('riseMs'),
        dropMs: num('dropMs'),
        hangMs: optNum('hangMs', 0),
        radius: optNum('radius', 0),
        knockback: optNum('knockback', 0),
        knockbackMs: optNum('knockbackMs', 220),
        slams: optNum('slams', 1),
        intervalMs: optNum('intervalMs', 0),
      };
      return params;
    }
  }
}

/** `folkvangr_attack_pattern` 한 줄 → 검증된 패턴 정의. */
export function folkvangrAttackPatternFromRaw(
  row: RawFolkvangrAttackPatternRow
): FolkvangrAttackPatternData {
  const id = Number(row.id);
  const type = String(row.type) as FolkvangrAttackType;
  if (!(FOLKVANGR_ATTACK_TYPES as readonly string[]).includes(type)) {
    throw new Error(
      `folkvangr_attack_pattern(id=${id}).type: 알 수 없는 값 "${String(row.type)}" ` +
        `— 허용: ${FOLKVANGR_ATTACK_TYPES.join(', ')}`
    );
  }
  const params = assertPatternParams(id, type, row.params);
  // `type` 과 `params` 를 함께 좁히기 위한 단언. 위 switch 가 타입별로 정확히 대응하는
  // 형태만 돌려주므로 안전하다 — TS 는 두 값이 같은 case 에서 나왔음을 추적하지 못한다.
  return { id, name: String(row.name ?? ''), type, params } as FolkvangrAttackPatternData;
}

/**
 * 보스 타임라인 조립 — `folkvangr_boss_pattern` 행에 패턴 정의를 조인한다.
 *
 * 정의가 없는 `pattern_id` 는 던진다. 조용히 건너뛰면 "보스가 그 공격만 안 한다" 가
 * 되어, 데이터를 지운 사람도 플레이한 사람도 알아채지 못한다.
 */
function buildBossPatterns(
  bossId: number,
  rows: RawFolkvangrBossPatternRow[],
  library: Map<number, FolkvangrAttackPatternData>
): FolkvangrBossPatternEntry[] {
  return rows
    .filter((r) => Number(r.boss_id) === bossId)
    .sort((a, b) => Number(a.at_sec) - Number(b.at_sec))
    .map((r) => {
      const patternId = Number(r.pattern_id);
      const pattern = library.get(patternId);
      if (!pattern) {
        throw new Error(
          `folkvangr_boss_pattern(boss_id=${bossId}, at_sec=${r.at_sec}): ` +
            `folkvangr_attack_pattern(id=${patternId}) 정의가 없다`
        );
      }
      const entry: FolkvangrBossPatternEntry = {
        atSec: Number(r.at_sec),
        damage: Number(r.damage),
        pattern,
      };
      if (r.repeat_every_sec !== null && r.repeat_every_sec !== undefined) {
        entry.repeatEverySec = Number(r.repeat_every_sec);
      }
      if (r.repeat_count !== null && r.repeat_count !== undefined) {
        entry.repeatCount = Number(r.repeat_count);
      }
      return entry;
    });
}

export function folkvangrBossFromRaw(
  row: RawFolkvangrBossRow,
  raw: Pick<AllRawData, 'folkvangr_boss_pattern' | 'folkvangr_attack_pattern'>
): FolkvangrBossData {
  const patternRows = (raw.folkvangr_boss_pattern ?? []) as unknown as RawFolkvangrBossPatternRow[];
  const libraryRows = (raw.folkvangr_attack_pattern ??
    []) as unknown as RawFolkvangrAttackPatternRow[];
  const library = new Map<number, FolkvangrAttackPatternData>();
  for (const def of libraryRows) {
    library.set(Number(def.id), folkvangrAttackPatternFromRaw(def));
  }

  const boss: FolkvangrBossData = {
    id: Number(row.id),
    nameKey: String(row.name_key),
    blessedDamageMul: Number(row.blessed_damage_mul ?? 1),
    blessedHpMul: Number(row.blessed_hp_mul ?? 1),
    durationSec: Number(row.duration_sec),
    scoreMul: Number(row.score_mul ?? 1),
    patterns: buildBossPatterns(Number(row.id), patternRows, library),
  };
  const resourceId = optText(row.resource_id);
  if (resourceId) boss.resourceId = resourceId;
  const bg = optText(row.bg);
  if (bg) boss.bg = bg;
  if (row.blessed_meta_tag_id !== null && row.blessed_meta_tag_id !== undefined) {
    boss.blessedMetaTagId = Number(row.blessed_meta_tag_id);
  }
  return boss;
}

export function folkvangrGradeStatFromRaw(row: RawFolkvangrGradeStatRow): FolkvangrGradeStatData {
  return {
    grade: Number(row.grade),
    baseDps: Number(row.base_dps),
    baseHp: Number(row.base_hp),
    levelDpsK: Number(row.level_dps_k ?? 0),
    levelHpK: Number(row.level_hp_k ?? 0),
    btDpsK: Number(row.bt_dps_k ?? 0),
    btHpK: Number(row.bt_hp_k ?? 0),
  };
}

/**
 * effects 원소의 `type` 을 union 에 대조한다.
 *
 * DB 제약은 `jsonb_typeof(effects) = 'array'` 뿐이라 오타(`GLOBAL_DMG_MUL`)가
 * 그대로 통과한다. 통과하면 applyStatOmens 의 `default: break` 가 조용히 무시해
 * "아무 일도 안 하는 룬" 이 라이브로 나간다 — folkvangrRewardFromRaw 의
 * rank_type 검증과 같은 이유로 여기서 던진다.
 */
function assertRuneEffects(runeId: number, effects: unknown): FolkvangrOmenEffect[] {
  if (effects == null) return [];
  if (!Array.isArray(effects)) {
    throw new Error(`folkvangr_omen(id=${runeId}).effects: 배열이 아니다 — ${typeof effects}`);
  }
  const known = new Set<string>(FOLKVANGR_EFFECT_TYPES);
  return effects.map((eff, i) => {
    if (eff == null || typeof eff !== 'object' || Array.isArray(eff)) {
      throw new Error(`folkvangr_omen(id=${runeId}).effects[${i}]: 객체가 아니다`);
    }
    const type = (eff as { type?: unknown }).type;
    if (typeof type !== 'string' || !known.has(type)) {
      throw new Error(
        `folkvangr_omen(id=${runeId}).effects[${i}].type: 알 수 없는 값 "${String(type)}" ` +
          `— 허용: ${FOLKVANGR_EFFECT_TYPES.join(', ')}`
      );
    }
    return eff as FolkvangrOmenEffect;
  });
}

export function folkvangrOmenFromRaw(row: RawFolkvangrOmenRow): FolkvangrOmenData {
  const omen: FolkvangrOmenData = {
    id: Number(row.id),
    omenPoolId: Number(row.omen_pool_id),
    nameKey: String(row.name_key),
    descriptionKey: String(row.description_key),
    effects: assertRuneEffects(Number(row.id), row.effects),
    selectionWeight: Number(row.selection_weight ?? 1),
    isActive: Boolean(row.is_active),
  };
  const icon = optText(row.icon);
  if (icon) omen.icon = icon;
  return omen;
}

/**
 * season 행 → FolkvangrSeasonDef.
 *
 * 한때 2번째 인자로 `folkvangr_schedule` 행을 받아 시즌 안에서 일자별 편성을 접었다.
 * 그 주기는 이제 시즌 자체의 길이가 정하므로(하루짜리 시즌을 이어 붙인다) 시즌 행이
 * 보스·계시·축복을 직접 든다.
 */
export function folkvangrSeasonFromRaw(row: RawFolkvangrSeasonRow): FolkvangrSeasonDef {
  const season: FolkvangrSeasonDef = {
    id: Number(row.id),
    name: String(row.name),
    startTime: parseEpoch(String(row.start_time), 'start_time'),
    endTime: parseEpoch(String(row.end_time), 'end_time'),
    omenPoolId: Number(row.omen_pool_id),
    // trim 하지 않으면 어드민에서 손으로 친 free-text 공백 하나로 folkvangr_reward
    // 쪽 rewardTableId 와 조용히 어긋나 보상 탭이 에러 없이 빈 상태가 된다(아래
    // folkvangrRewardFromRaw 의 동일 관용 참조).
    rewardTableId: String(row.reward_table_id).trim(),
    bossId: Number(row.boss_id),
    playReward: row.play_reward ?? null,
    dailyFreeCount: Number(row.daily_free_count ?? 0),
    dailyAdCount: Number(row.daily_ad_count ?? 0),
    dailyTicketMax: Number(row.daily_ticket_max ?? 0),
    probeSampleCount: Number(row.probe_sample_count ?? 100),
    probeDensity: Number(row.probe_density ?? 2),
    resetHourUtc: Number(row.reset_hour_utc ?? 0),
  };
  // 비어 있으면 필드 자체를 싣지 않는다 — undefined 를 실어 보내면 튜플 왕복에서
  // null 로 되살아나 "지정됨" 과 구분이 안 된다.
  if (row.omen_id !== null && row.omen_id !== undefined) season.omenId = Number(row.omen_id);
  if (row.blessed_meta_tag_id !== null && row.blessed_meta_tag_id !== undefined) {
    season.blessedMetaTagId = Number(row.blessed_meta_tag_id);
  }
  return season;
}

/**
 * `folkvangr_reward.rewards` 는 JSONB 라 숫자가 아닌 값(문자열·객체·null)이 들어올 수
 * 있다 — 좁히지 않으면 화면(랭킹 팝업의 보상 구간 헤더)이 무검증 캐스팅에 기대
 * `[object Object]` 를 그대로 노출한다. 숫자 문자열(`"100"`)은 강제 변환하고,
 * 변환 불가한 항목은 조용히 제외한다(리그 `leagueRewardTierFromRaw` 와 달리 여기는
 * 항목 단위 오염을 막는 게 목적이라 통째로 던지지 않는다).
 */
function coerceRewardAmounts(raw: Record<string, unknown> | null): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw) return out;
  for (const [key, value] of Object.entries(raw)) {
    const n = typeof value === 'string' ? Number(value) : value;
    if (typeof n === 'number' && Number.isFinite(n)) out[key] = n;
  }
  return out;
}

export function folkvangrRewardFromRaw(row: RawFolkvangrRewardRow): FolkvangrRewardTierData {
  const rankType = String(row.rank_type);
  if (rankType !== 'ABSOLUTE' && rankType !== 'PERCENTILE') {
    throw new Error(`folkvangr_reward.rank_type: 알 수 없는 값 "${rankType}"`);
  }
  return {
    // trim 하지 않으면 어드민에서 손으로 친 free-text 에 공백 하나만 섞여도
    // 시즌의 rewardTableId 와 조용히 안 맞아 보상 탭이 에러 없이 빈 상태가 된다
    // (리그 leagueRewards 의 동일 관용 — supabase/functions/_shared/build-json.ts 참조).
    rewardTableId: String(row.reward_table_id).trim(),
    tierOrder: Number(row.tier_order),
    rankType: rankType as FolkvangrRankType,
    rankFrom: Number(row.rank_from),
    rankTo: Number(row.rank_to),
    rewards: coerceRewardAmounts(row.rewards),
  };
}

registerCategorySchema('serverFolkvangrBosses', FolkvangrBossSchema);
registerCategorySchema('serverFolkvangrGradeStats', FolkvangrGradeStatSchema);
registerCategorySchema('serverFolkvangrRunes', FolkvangrOmenSchema);
registerCategorySchema('serverFolkvangrSeasons', FolkvangrSeasonSchema);
